import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n, { DEFAULT_LANGUAGE, Language, SUPPORTED_LANGUAGES } from "./index";

/**
 * Detect the active language from the current URL pathname.
 * /sv or /sv/... => "sv", otherwise "en".
 */
export const getLanguageFromPath = (pathname: string): Language => {
  const segs = pathname.split("/").filter(Boolean);
  const first = segs[0];
  if (first && (SUPPORTED_LANGUAGES as readonly string[]).includes(first) && first !== DEFAULT_LANGUAGE) {
    return first as Language;
  }
  return DEFAULT_LANGUAGE;
};

/**
 * Strip a known language prefix from a pathname.
 * "/sv/about" -> "/about", "/sv" -> "/", "/about" -> "/about"
 */
export const stripLangPrefix = (pathname: string): string => {
  const segs = pathname.split("/").filter(Boolean);
  if (segs[0] && (SUPPORTED_LANGUAGES as readonly string[]).includes(segs[0])) {
    const rest = "/" + segs.slice(1).join("/");
    return rest === "/" ? "/" : rest.replace(/\/$/, "");
  }
  return pathname;
};

/**
 * Prefix a path with the language segment when needed (default lang has no prefix).
 */
export const buildLocalizedPath = (path: string, lang: Language): string => {
  // External links and hashes pass through unchanged
  if (!path.startsWith("/")) return path;
  const base = stripLangPrefix(path);
  if (lang === DEFAULT_LANGUAGE) return base;
  if (base === "/") return `/${lang}`;
  return `/${lang}${base}`;
};

/** Hook returning the current language + helpers. */
export const useLanguage = () => {
  const { pathname, search, hash } = useLocation();
  const navigate = useNavigate();
  const { i18n: i18nInstance } = useTranslation();
  const lang = getLanguageFromPath(pathname);

  useEffect(() => {
    if (i18nInstance.language !== lang) {
      i18nInstance.changeLanguage(lang);
    }
    if (typeof document !== "undefined") {
      document.documentElement.lang = lang;
    }
  }, [lang, i18nInstance]);

  const switchTo = (target: Language) => {
    const scrollY = window.scrollY;
    const base = stripLangPrefix(pathname);
    const next =
      target === DEFAULT_LANGUAGE
        ? base + search + hash
        : `/${target}${base === "/" ? "" : base}` + search + hash;
    navigate(next);
    // React Router resets scroll on navigation; restore position so the
    // language switch feels like an in-place translation, not a page jump.
    setTimeout(() => {
      window.scrollTo(0, scrollY);
    }, 0);
  };

  const localize = (path: string) => buildLocalizedPath(path, lang);

  return { lang, switchTo, localize };
};

/** Initialize i18n side-effect import. */
export { i18n };
